/**
 * Monitoring API for web mode
 */

import { httpClient } from './httpClient'
import type { ApiResponse } from '../types'
import type { components } from '../../../../../types/flex-api'

type PoolState = components['schemas']['PoolState']
type ChargingSession = components['schemas']['ChargingSession']
type SystemChargingSession = components['schemas']['SystemChargingSession']
export type GCPForecastPeriod = components['schemas']['GCPForecastPeriod']
export type GCPForecast = components['schemas']['GCPForecast']
export type GCPPoolForecasts = components['schemas']['GCPPoolForecasts']
export type GCPForecastPurpose = components['schemas']['GCPForecastPurpose']

// Flexibility Band Response Types (from API spec)
export interface PoolFlexBandResponse {
  poolID: string
  time: string
  powerUnit: string
  energyUnit: string
  stateOfChargeUnit: string
  ev: {
    currentState: {
      activePower: number
      activePowerSetpoint: number
      aggregatedEnergyConsumption?: number
    }
    flexBand: Array<{
      startAt: string
      endAt: string
      eMax: number
      eMin: number
      eMaxRange: number
      eMinRange: number
      eBaseline: number
      /** Day-ahead electricity price in EUR/kWh (optional — not all pools have this). */
      daPriceEurKwh?: number
    }>
  }
  meter: {
    activePower: number
    aggregatedEnergyConsumption: number
  }
}

/**
 * Get current state of a pool
 */
export async function getPoolState(poolID: string): Promise<ApiResponse<PoolState>> {
  try {
    const response = await httpClient.getClient().get<PoolState>(`/pools/${poolID}/current-state`)
    return {
      success: true,
      data: response.data,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get pool state',
      errorCode: error.code || 'UNKNOWN_ERROR',
      details: error.details,
    }
  }
}

/**
 * Get flexibility bands for a pool
 */
export async function getFlexBands(
  poolID: string,
  forecastDuration?: number
): Promise<ApiResponse<PoolFlexBandResponse>> {
  try {
    const params = forecastDuration ? { forecastDuration } : undefined
    const response = await httpClient
      .getClient()
      .get<PoolFlexBandResponse>(`/pools/${poolID}/flexibility-bands`, { params })
    return {
      success: true,
      data: response.data,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get flexibility bands',
      errorCode: error.code || 'UNKNOWN_ERROR',
      details: error.details,
    }
  }
}

/**
 * Get charging sessions for a pool
 */
export async function getChargingSessions(
  poolID: string,
  params?: {
    start?: string
    end?: string
    include?: string
  }
): Promise<ApiResponse<ChargingSession[]>> {
  try {
    const response = await httpClient
      .getClient()
      .get<{
        chargingSessions: ChargingSession[]
      }>(`/pools/${poolID}/charging-sessions`, { params })
    return {
      success: true,
      data: response.data.chargingSessions || [],
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get charging sessions',
      errorCode: error.code || 'UNKNOWN_ERROR',
      details: error.details,
    }
  }
}

export async function getSystemChargingSessions(
  systemID: string,
  params: { start: string; end: string }
): Promise<ApiResponse<SystemChargingSession[]>> {
  try {
    const response = await httpClient
      .getClient()
      .get<{
        chargingSessions: SystemChargingSession[]
      }>(`/pools/systems/${systemID}/charging-sessions`, { params })
    return {
      success: true,
      data: response.data.chargingSessions || [],
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get system charging sessions',
      errorCode: error.code || 'UNKNOWN_ERROR',
      details: error.details,
    }
  }
}

// PID Controller State (internal endpoint for imbalance/tou_stacking pools)
export interface PIDControllerState {
  PCoefficient: number
  ICoefficient: number
  DCoefficient: number
  previousTarget: number
  previousControlError: number
  controlErrorIntegral: number
  controlBand: number
  softResetFactor: number
  modifiedAt: string
}

/**
 * Get PID controller state for a pool
 * Only available for pools with purpose: imbalance or tou_stacking_poc
 */
export async function getPIDState(poolID: string): Promise<ApiResponse<PIDControllerState>> {
  try {
    const response = await httpClient.getClient().get<PIDControllerState>(`/pools/${poolID}/pid`)
    return {
      success: true,
      data: response.data,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get PID controller state',
      errorCode: error.code || 'UNKNOWN_ERROR',
      details: error.details,
    }
  }
}

/**
 * Get GCP forecast data for a pool.
 * Returns the latest 48h forecast per active GCP purpose.
 * Optionally filter by one or more purposes.
 */
export async function getPoolGCPForecasts(
  poolID: string,
  purposes?: GCPForecastPurpose[]
): Promise<ApiResponse<GCPPoolForecasts>> {
  try {
    const params: Record<string, string> = {}
    if (purposes && purposes.length > 0) {
      // The API accepts repeated purpose query params; axios serialises array params with the same key
      params['purpose'] = purposes as any
    }
    const response = await httpClient
      .getClient()
      .get<GCPPoolForecasts>(`/pools/${poolID}/forecasts/gcp`, {
        params,
        // axios needs to repeat the key for array params
        paramsSerializer: (p: Record<string, string | string[]>) => {
          const parts: string[] = []
          for (const [key, value] of Object.entries(p)) {
            if (Array.isArray(value)) {
              value.forEach((v) => parts.push(`${key}=${encodeURIComponent(v)}`))
            } else {
              parts.push(`${key}=${encodeURIComponent(value as string)}`)
            }
          }
          return parts.join('&')
        },
      })
    return {
      success: true,
      data: response.data,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get GCP forecasts',
      errorCode: error.code || 'UNKNOWN_ERROR',
      details: error.details,
    }
  }
}
